
import { Badge } from "@/components/ui/badge";
import { KnowledgeEntry, SearchResult } from "../types";
import { FileText, Search, CheckCircle2 } from "lucide-react";

interface EntryStatusBadgeProps {
  entry?: KnowledgeEntry | SearchResult;
  status?: 'pending' | 'completed' | 'failed';
}

export function EntryStatusBadge({ entry, status }: EntryStatusBadgeProps) {
  // If we have a status directly passed, use that
  if (status) {
    return (
      <Badge 
        variant={status === 'completed' ? 'default' : 'outline'}
        className={status === 'completed' ? 'bg-green-500 hover:bg-green-600' : ''}
      >
        {status === 'pending' ? 'Processing' : 
         status === 'completed' ? 'Completed' : 
         'Failed'}
      </Badge>
    );
  }

  // If an entry is passed instead, extract status and other information from it
  if (!entry) return null;

  const isSearchResult = 'match_type' in entry;

  if (isSearchResult) {
    const searchResult = entry as SearchResult;
    return (
      <>
        <Badge variant="outline">
          {searchResult.match_type || 'match'}
        </Badge>
        {searchResult.score && (
          <Badge variant="secondary">
            {Math.round((searchResult.score) * 100)}% match
          </Badge>
        )}
        {/* Use similarity only if it exists */}
        {searchResult.similarity !== undefined && (
          <Badge variant="secondary">
            {Math.round(searchResult.similarity * 100)}% similarity
          </Badge>
        )}
      </>
    );
  }

  const hasPdf = entry.file_url && entry.file_type?.includes('pdf');
  
  // Make sure metadata exists and is safely accessed
  const metadata = entry.metadata || {};
  // Type-safe access to metadata properties
  const pdfType = metadata.pdf_type as string | undefined;
  const ocrUsed = metadata.ocr_used as boolean | undefined;
  const ocrSuccess = metadata.ocr_success as boolean | undefined;

  return (
    <div className="flex gap-2">
      <Badge 
        variant={entry.embedding_status === 'completed' ? 'default' : 'outline'}
        className={entry.embedding_status === 'completed' ? 'bg-green-500 hover:bg-green-600' : ''}
      >
        {entry.embedding_status === 'pending' ? 'Processing' : 
         entry.embedding_status === 'completed' ? 'Completed' : 
         'Failed'}
      </Badge>

      {hasPdf && pdfType && (
        <Badge 
          variant="outline" 
          className="flex items-center gap-1"
        >
          <FileText className="h-3 w-3" />
          {pdfType}
        </Badge>
      )}

      {hasPdf && ocrUsed && (
        <Badge 
          variant={!ocrSuccess ? "destructive" : "secondary"}
          className="flex items-center gap-1"
        >
          <Search className="h-3 w-3" />
          {!ocrSuccess ? 'OCR Failed' : 'OCR Success'}
        </Badge>
      )}
    </div>
  );
}
